import React, { useMemo, useEffect } from "react";
import { useData } from "../../context/DataContext";
import { useAuth } from "../../context/AuthContext";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { Card, CardContent } from "../ui/card";
import { calculateQualificationStatus } from "../../utils/qualification-utils";
import { Qualification } from "../../types";
import { QualificationsListView } from "./QualificationsListView";

interface QualificationsTabProps {
  userId: string;
}

export const QualificationsTab: React.FC<QualificationsTabProps> = ({ userId }) => {
  const { qualifications = [], fetchQualifications } = useData() || {};
  const { currentUser } = useAuth();
  const ability = useAbility(AbilityContext);

  useEffect(() => {
    fetchQualifications && fetchQualifications();
  }, []);

  // Determine if the view should be read-only
  const isReadOnly = useMemo(() => {
    const isOwner = String(currentUser?.id) === String(userId);
    const hasUpdatePermission = ability.can(ACTIONS.UPDATE, RESOURCES.QUALIFICATION);
    
    // Not read-only if owner OR has explicit permission
    return !(isOwner || hasUpdatePermission);
  }, [currentUser, userId, ability]);

  // Filter qualifications for this specific user
  const userQualifications = useMemo(() => {
    return (qualifications || []).filter((q: Qualification) => {
      const personId = q.person && typeof q.person === 'object' ? q.person.id : q.person;
      return String(personId) === String(userId) && !q.deleted;
    });
  }, [userId, qualifications]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Qualifications</h2>
          <p className="text-sm text-muted-foreground">
            Manage licenses, certifications, and accreditations
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">Total</div>
            <div className="text-2xl font-bold text-slate-900">{userQualifications.length}</div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">Active</div>
            <div className="text-2xl font-bold text-green-600">
              {userQualifications.filter(q => calculateQualificationStatus(q) === "Active").length}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">Expiring Soon</div>
            <div className="text-2xl font-bold text-orange-600">
              {userQualifications.filter(q => calculateQualificationStatus(q) === "Expiring Soon").length}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">Expired</div>
            <div className="text-2xl font-bold text-red-600">
              {userQualifications.filter(q => calculateQualificationStatus(q) === "Expired").length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* List View */}
      <QualificationsListView 
        qualifications={userQualifications} 
        showStaffColumn={false} 
        isReadOnly={isReadOnly}
      />
    </div>
  );
};
